import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { getFieldsForArena, type FieldListItem } from '@/services/fieldApiService';
import { ProductApiService } from '@/services/productApiService';
import { fieldDetailApiService } from '@/services/fieldDetailApiService';
import { useToast } from '@/hooks/useToast';
import authService from '@/services/authService';
import { getArenaSlug } from '@/utils/arena';
import { getAdminBasePath } from '@/utils/adminPaths';

export type FieldRow = {
  image: string;
  title: string;
  price: string;
  category: string;
  status: string;
  raw: FieldListItem;
};

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'Terjadi kesalahan';
};

const getCategoryIcon = (catName?: string) => {
  const c = (catName || '').toLowerCase();
  if (c.includes('futsal') || c.includes('soccer') || c.includes('sepak')) return 'ph:soccer-ball-bold';
  if (c.includes('badminton') || c.includes('bulutangkis')) return 'ph:baseball-bold';
  if (c.includes('padel') || c.includes('tennis') || c.includes('tenis')) return 'ph:tennis-ball-bold';
  if (c.includes('basket')) return 'ph:basketball-bold';
  if (c.includes('voli') || c.includes('volley')) return 'ph:volleyball-bold';
  return 'ph:trophy-bold';
};

const getFieldSpec = (raw: FieldListItem) => {
  // 1. Surface
  let surface = raw.surface_type;
  if (!surface) {
    const cat = (raw.category || '').toLowerCase();
    if (cat.includes('badminton')) surface = 'Karpet vinyl';
    else if (cat.includes('futsal')) surface = 'Vinyl';
    else if (cat.includes('padel') || cat.includes('tennis')) surface = 'Rumput sintetis';
    else surface = 'Vinyl';
  }

  // 2. Type (Indoor / Outdoor / Hybrid)
  let fType = raw.field_type || raw.type || 'Indoor';
  fType = fType.charAt(0).toUpperCase() + fType.slice(1).toLowerCase();

  // 3. Size
  let size = raw.size;
  if (!size) {
    const cat = (raw.category || '').toLowerCase();
    if (cat.includes('badminton')) size = '13,4 × 6,1 m';
    else if (cat.includes('padel')) size = '20 × 10 m';
    else if (cat.includes('futsal')) size = '25 × 15 m';
    else size = '25 × 15 m';
  }

  return `${surface} · ${fType} · ${size}`;
};

const getFieldHours = (raw: FieldListItem) => {
  let hours = '07.00 - 23.00';
  if (raw.open_time && raw.close_time) {
    const open = raw.open_time.slice(0, 5).replace(':', '.');
    const close = raw.close_time.slice(0, 5).replace(':', '.');
    hours = `${open} - ${close}`;
  }

  let duration = '1 jam/sesi';
  if (raw.slot_duration_minutes && raw.slot_duration_minutes > 0) {
    const hoursNum = raw.slot_duration_minutes / 60;
    duration = `${hoursNum} jam/sesi`;
  }

  return `${hours} · ${duration} · Multi sesi`;
};

const getFieldPrice = (raw: FieldListItem) => {
  const minPrice = raw.price;
  const maxPrice = raw.max_price;

  if (minPrice && maxPrice && maxPrice > minPrice) {
    return `Rp. ${minPrice.toLocaleString('id-ID')} – ${maxPrice.toLocaleString('id-ID')}`;
  }
  if (minPrice && minPrice > 0) {
    return `Rp. ${minPrice.toLocaleString('id-ID')}`;
  }
  return 'Rp. 150.000';
};

const FieldList: React.FC = () => {
  const params = useParams<{ slug?: string }>();
  const [rows, setRows] = useState<FieldRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(12); // Max 12 per page
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [activeMenuSlug, setActiveMenuSlug] = useState<string | null>(null);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState<FieldRow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const menuContainerRef = useRef<HTMLDivElement | null>(null);

  const toast = useToast();
  const navigate = useNavigate();

  const arenaSlug = params.slug || getArenaSlug() || localStorage.getItem('arenaSlug') || '';
  const adminBase = getAdminBasePath(arenaSlug);
  const canManageField = authService.getRole() === 'owner' || authService.getRole() === 'staff';

  // Close active dropdown menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (activeMenuSlug && !menuContainerRef.current?.contains(e.target as Node)) {
        setActiveMenuSlug(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeMenuSlug]);

  const toggleStatus = (field: FieldRow) => async (newIsActive: boolean) => {
    if (!field.raw?.slug) {
      toast.error('Field ID tidak ditemukan', 'Gagal mengupdate status field');
      return;
    }

    try {
      await ProductApiService.updateFieldVisibility(field.raw.slug, newIsActive);
      setRows((r) =>
        r.map((row) =>
          row.raw.slug === field.raw.slug
            ? {
                ...row,
                status: newIsActive ? 'Aktif' : 'Nonaktif',
                raw: { ...row.raw, is_active: newIsActive, status: newIsActive ? 'Aktif' : 'Nonaktif' },
              }
            : row
        )
      );

      toast.success(
        `Field "${field.title}" berhasil di${newIsActive ? 'aktifkan' : 'nonaktifkan'}`
      );
    } catch (err: unknown) {
      toast.error(getErrorMessage(err), 'Gagal mengupdate status field');
    }
  };

  const handleDeleteField = async () => {
    if (!fieldToDelete) return;

    setIsDeleting(true);
    try {
      const response = await fieldDetailApiService.deleteField(arenaSlug, fieldToDelete.raw.slug);

      if (response.success) {
        setRows((prevRows) => prevRows.filter((row) => row.raw.slug !== fieldToDelete.raw.slug));
        setTotalItems((prev) => Math.max(0, prev - 1));
        toast.success(`Field "${fieldToDelete.title}" berhasil dihapus`);
      } else {
        throw new Error(response.message || 'Failed to delete field');
      }
    } catch (err: unknown) {
      toast.error(getErrorMessage(err) || 'Gagal menghapus field');
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
      setFieldToDelete(null);
    }
  };

  const handleOpenDeleteDialog = (field: FieldRow) => {
    setFieldToDelete(field);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    if (isDeleting) return;
    setIsDeleteDialogOpen(false);
    setFieldToDelete(null);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getFieldsForArena(arenaSlug, page, limit, search || undefined);
        if (res && res.success) {
          const fields = res.data.fields || [];
          setRows(
            fields.map((f) => {
              let imageSrc = '';
              if (Array.isArray(f.images) && f.images.length > 0 && typeof f.images[0] === 'string' && f.images[0].trim() !== '') {
                imageSrc = f.images[0];
              } else if (f.image && typeof f.image === 'string' && f.image.trim() !== '') {
                imageSrc = f.image;
              }

              const isActive = typeof f.status === 'boolean' ? f.status : f.status === 'Aktif' || f.is_active;

              return {
                image: imageSrc,
                title: f.name || f.title || '—',
                price: getFieldPrice(f),
                category: f.category || f.type || '—',
                status: isActive ? 'Aktif' : 'Nonaktif',
                raw: f,
              };
            })
          );

          const serverTotalPages = res.data?.pagination?.total_pages || 1;
          const serverTotalItems = res.data?.pagination?.total_items || fields.length;
          setTotalPages(serverTotalPages);
          setTotalItems(serverTotalItems);
        } else {
          setError(res?.message || 'Failed to load fields');
        }
      } catch (err: unknown) {
        setError(getErrorMessage(err) || 'Error fetching fields');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [arenaSlug, page, limit, search]);

  const handleSearchInput = (value: string) => {
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearch(value);
      setPage(1);
    }, 400);
  };

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  // Pagination page numbers generator (‹ 1 2 3 … 33 ›)
  const getPaginationItems = (currentPage: number, total: number) => {
    if (total <= 5) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    const pages: (number | string)[] = [];
    pages.push(1);
    if (currentPage > 3) {
      pages.push('dots-1');
    }
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(total - 1, currentPage + 1);
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    if (currentPage < total - 2) {
      pages.push('dots-2');
    }
    pages.push(total);
    return pages;
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 bg-[#F5F6F7] min-h-screen">
      {/* Header Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-xl sm:text-2xl font-semibold text-[#0E1726]">Lapangan</h1>

        <div className="flex items-center gap-3">
          {/* Search Bar matching Sporto Design System */}
          <div className="relative w-full sm:w-64 md:w-72">
            <Icon
              icon="ph:magnifying-glass"
              className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C8C8C]"
            />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => handleSearchInput(e.target.value)}
              placeholder="Cari lapangan..."
              className="w-full h-10 pl-10 pr-4 text-xs sm:text-sm bg-white border border-[#E0E6ED] rounded-full text-[#0E1726] placeholder-[#8C8C8C] shadow-2xs focus:outline-none focus:border-[#FC480D] transition-colors"
            />
          </div>

          {canManageField && (
            <button
              onClick={() => navigate(`${adminBase}/field/add`)}
              className="h-10 px-4 sm:px-5 inline-flex items-center gap-2 rounded-full bg-[#FC480D] hover:bg-[#E8490F] text-white text-xs sm:text-sm font-semibold shadow-xs transition-colors shrink-0"
            >
              <Icon icon="ph:plus-bold" className="w-4 h-4" />
              <span>Tambah Lapangan</span>
            </button>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      {loading ? (
        <div className="flex items-center justify-center p-16">
          <div className="flex flex-col items-center gap-3">
            <Icon icon="ph:spinner" className="w-8 h-8 text-[#FC480D] animate-spin" />
            <span className="text-sm text-[#8C8C8C]">Memuat daftar lapangan...</span>
          </div>
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-600">
          {error}
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-[#E0E6ED] bg-white p-16 text-center">
          <Icon icon="ph:soccer-ball" className="w-12 h-12 text-[#98A2B3] mx-auto mb-3" />
          <h3 className="text-base font-semibold text-[#0E1726]">Belum ada lapangan</h3>
          <p className="text-xs text-[#8C8C8C] mt-1 max-w-sm mx-auto">
            {search ? 'Tidak ada lapangan yang sesuai dengan pencarian.' : 'Mulai dengan menambahkan lapangan pertama di arena ini.'}
          </p>
          {canManageField && !search && (
            <button
              onClick={() => navigate(`${adminBase}/field/add`)}
              className="mt-4 h-9 px-4 inline-flex items-center gap-1.5 rounded-full bg-[#FC480D] hover:bg-[#E8490F] text-white text-xs font-semibold shadow-xs transition-colors"
            >
              <Icon icon="ph:plus-bold" className="w-3.5 h-3.5" />
              <span>Tambah Lapangan</span>
            </button>
          )}
        </div>
      ) : (
        <div ref={menuContainerRef} className="space-y-6">
          {/* Responsive Grid with default 6 cards per row on desktop */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 min-[1360px]:grid-cols-6 gap-4">
            {rows.map((field) => {
              const isNonaktif = field.status === 'Nonaktif';
              const specText = getFieldSpec(field.raw);
              const hoursText = getFieldHours(field.raw);
              const priceText = getFieldPrice(field.raw);
              const isMenuOpen = activeMenuSlug === field.raw.slug;

              return (
                <div
                  key={field.raw.slug}
                  className={`bg-white rounded-2xl border border-[#E0E6ED] transition-all duration-200 hover:border-[#D5D5D5] hover:shadow-sm flex flex-col ${
                    isNonaktif ? 'opacity-90' : ''
                  }`}
                >
                  {/* Top Photo / Media Area (16:9) */}
                  <div className="relative aspect-[16/9] w-full bg-[#F5F6F7] rounded-t-2xl overflow-hidden select-none">
                    {/* Status Pill Badge */}
                    <div className="absolute top-2.5 left-2.5 z-10">
                      {isNonaktif ? (
                        <span className="inline-flex items-center rounded-full border border-[#E0E6ED] bg-[#F5F6F7] px-2.5 py-0.5 text-[11px] font-semibold text-[#8C8C8C]">
                          Nonaktif
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full border border-[#0BCF46] bg-[#ECFDF3] px-2.5 py-0.5 text-[11px] font-semibold text-[#027A48]">
                          Aktif
                        </span>
                      )}
                    </div>

                    {/* Image / 16:9 Placeholder */}
                    {field.image && !field.image.includes('placeholder') && field.image !== '/sporto-logo.png' ? (
                      <img
                        src={field.image}
                        alt={field.title}
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                      />
                    ) : (
                      <div className="absolute inset-2.5 rounded-xl border border-dashed border-[#D5D5D5] bg-[#F6F6F6] flex flex-col items-center justify-center gap-1 text-center pointer-events-none">
                        <Icon icon="ph:image-square" className="w-6 h-6 text-[#98A2B3]" />
                        <span className="text-[11px] text-[#919191] font-medium leading-none">
                          Foto lapangan 16:9
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Card Body */}
                  <div className="p-3.5 sm:p-4 flex flex-col justify-between flex-1">
                    <div>
                      {/* Sport Category Line */}
                      <div
                        className={`inline-flex items-center gap-1.5 text-[11.5px] font-semibold leading-none ${
                          isNonaktif ? 'text-[#8C8C8C]' : 'text-[#FC480D]'
                        }`}
                      >
                        <Icon icon={getCategoryIcon(field.category)} className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">{field.category}</span>
                      </div>

                      {/* Field Name */}
                      <h3
                        className="text-[14px] sm:text-[14.5px] font-semibold text-[#0E1726] mt-1.5 truncate"
                        title={field.title}
                      >
                        {field.title}
                      </h3>

                      {/* Specification Line (Surface · Type · Size) */}
                      <p
                        className="text-[11px] text-[#8C8C8C] mt-1 truncate"
                        title={specText}
                      >
                        {specText}
                      </p>

                      {/* Operating Hours & Session Details */}
                      <div className="flex items-center gap-1 text-[10.5px] text-[#8C8C8C] mt-1.5 truncate">
                        <Icon icon="ph:clock" className="w-3.5 h-3.5 text-[#919191] shrink-0" />
                        <span className="truncate">{hoursText}</span>
                      </div>
                    </div>

                    {/* Bottom Row: Price & 3-dots Menu */}
                    <div className="mt-3.5 pt-3 border-t border-[#F5F6F7] flex items-center justify-between">
                      <div className="min-w-0 pr-2">
                        <span className="text-[10px] text-[#8C8C8C] block leading-none">Harga per sesi</span>
                        <span className="text-[12px] sm:text-[12.5px] font-semibold text-[#0E1726] mt-0.5 block truncate font-data">
                          {priceText}
                        </span>
                      </div>

                      {/* 3-dots Action Menu */}
                      <div className="relative shrink-0">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveMenuSlug(isMenuOpen ? null : field.raw.slug);
                          }}
                          className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full border border-[#E0E6ED] bg-white hover:bg-[#F6F6F6] text-[#8C8C8C] hover:text-[#0E1726] flex items-center justify-center transition-colors shadow-2xs ${
                            isMenuOpen ? 'border-[#0E1726] text-[#0E1726]' : ''
                          }`}
                          aria-label="Aksi lapangan"
                        >
                          <Icon icon="ph:dots-three-vertical-bold" className="w-4 h-4" />
                        </button>

                        {/* Dropdown Popover */}
                        {isMenuOpen && (
                          <div className="absolute right-0 bottom-full mb-1.5 w-44 rounded-xl border border-[#E0E6ED] bg-white p-1 shadow-lg z-30">
                            <button
                              type="button"
                              onClick={() => {
                                setActiveMenuSlug(null);
                                const targetSlug = field.raw.physical_field?.slug || field.raw.slug;
                                navigate(`${adminBase}/booking?field=${targetSlug}`);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-[#0E1726] hover:bg-[#F6F6F6] rounded-lg transition-colors text-left"
                            >
                              <Icon icon="ph:calendar-blank-bold" className="w-4 h-4 text-[#8C8C8C]" />
                              <span>Jadwal & Kalender</span>
                            </button>

                            {canManageField && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setActiveMenuSlug(null);
                                    navigate(`${adminBase}/field/edit/${field.raw.slug}`);
                                  }}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-[#0E1726] hover:bg-[#F6F6F6] rounded-lg transition-colors text-left"
                                >
                                  <Icon icon="ph:pencil-simple-bold" className="w-4 h-4 text-[#8C8C8C]" />
                                  <span>Edit Lapangan</span>
                                </button>

                                <button
                                  type="button"
                                  onClick={() => {
                                    setActiveMenuSlug(null);
                                    void toggleStatus(field)(field.status !== 'Aktif');
                                  }}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-[#0E1726] hover:bg-[#F6F6F6] rounded-lg transition-colors text-left"
                                >
                                  <Icon
                                    icon={field.status === 'Aktif' ? 'ph:eye-slash-bold' : 'ph:eye-bold'}
                                    className="w-4 h-4 text-[#8C8C8C]"
                                  />
                                  <span>{field.status === 'Aktif' ? 'Nonaktifkan' : 'Aktifkan'}</span>
                                </button>

                                <div className="my-1 border-t border-[#F5F6F7]" />

                                <button
                                  type="button"
                                  onClick={() => {
                                    setActiveMenuSlug(null);
                                    handleOpenDeleteDialog(field);
                                  }}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors text-left"
                                >
                                  <Icon icon="ph:trash-bold" className="w-4 h-4 text-red-500" />
                                  <span>Hapus Lapangan</span>
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination bar matching Sporto Admin Design System */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-[#E0E6ED]">
              <div className="text-sm text-[#8C8C8C]">
                Menampilkan {totalItems > 0 ? (page - 1) * limit + 1 : 0} -{' '}
                {Math.min(page * limit, totalItems)} dari {totalItems} lapangan
              </div>

              <div className="flex items-center gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="w-10 h-10 rounded-full border border-[#D5D5D5] bg-white flex items-center justify-center text-[#1F1F1F] hover:bg-[#F6F6F6] disabled:opacity-45 disabled:cursor-not-allowed transition-colors"
                  aria-label="Previous Page"
                >
                  <Icon icon="ph:caret-left-bold" className="w-4 h-4" />
                </button>

                {getPaginationItems(page, totalPages).map((p, idx) =>
                  typeof p === 'number' ? (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`w-10 h-10 rounded-full text-sm font-semibold transition-colors ${
                        page === p
                          ? 'bg-[#FC480D] text-white shadow-xs'
                          : 'border border-[#D5D5D5] bg-white text-[#1F1F1F] hover:bg-[#F6F6F6]'
                      }`}
                    >
                      {p}
                    </button>
                  ) : (
                    <span
                      key={`dots-${idx}`}
                      className="w-10 h-10 flex items-center justify-center text-[#8C8C8C] text-sm"
                    >
                      …
                    </span>
                  )
                )}

                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="w-10 h-10 rounded-full border border-[#D5D5D5] bg-white flex items-center justify-center text-[#1F1F1F] hover:bg-[#F6F6F6] disabled:opacity-45 disabled:cursor-not-allowed transition-colors"
                  aria-label="Next Page"
                >
                  <Icon icon="ph:caret-right-bold" className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        title="Hapus Lapangan"
        message={`Apakah Anda yakin ingin menghapus lapangan "${fieldToDelete?.title}"? Tindakan ini tidak dapat dibatalkan.`}
        onConfirm={handleDeleteField}
        onCancel={handleCloseDeleteDialog}
        loading={isDeleting}
        confirmText="Hapus"
        cancelText="Batal"
      />
    </div>
  );
};

export default FieldList;
